import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMPTY = { name: '', email: '', password: '', phone: '', location: '' };

export default function ManageFeedbackTakers() {
  const [list, setList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    try {
      const [staffRes, locRes] = await Promise.all([
        api.get('/tenant/feedback-takers'),
        api.get('/locations'),
      ]);
      setList(staffRes.data.data);
      setLocations(locRes.data.data.filter(l => l.isActive));
    } catch { setError('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      if (editId) {
        const payload = { name: form.name, phone: form.phone, location: form.location || null };
        if (form.password) payload.password = form.password;
        await api.put(`/tenant/feedback-takers/${editId}`, payload);
        setSuccess('Staff member updated');
      } else {
        await api.post('/tenant/feedback-takers', { ...form, location: form.location || null });
        setSuccess('Staff member added');
      }
      setForm(EMPTY); setEditId(null); setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving');
    }
  };

  const handleEdit = (ft) => {
    setForm({ name: ft.name, email: ft.email, password: '', phone: ft.phone || '', location: ft.location?._id || '' });
    setEditId(ft._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try { await api.delete(`/tenant/feedback-takers/${id}`); setSuccess('Deleted'); load(); }
    catch { setError('Delete failed'); }
  };

  const handleToggleActive = async (ft) => {
    try { await api.put(`/tenant/feedback-takers/${ft._id}`, { isActive: !ft.isActive }); load(); }
    catch { setError('Update failed'); }
  };

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
        <div>
          <h3>Staff / Feedback Takers</h3>
          <p style={{color: 'var(--text-muted)', fontSize: 14}}>Your frontline team. Every piece of feedback flows through them.</p>
        </div>
        <button className="btn btn-primary" onClick={() => {
          setShowForm(!showForm);
          setEditId(null);
          setForm(EMPTY);
        }}>
          {showForm ? 'Cancel' : '+ Add Staff'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="card mb-3">
          <h4 style={{ marginBottom: 16 }}>{editId ? 'Edit Staff Member' : 'Add New Staff Member'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label>Name</label>
                <input className="form-control" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
              </div>
              <div className="form-group">
                <label>Phone (optional)</label>
                <input className="form-control" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1 234 567 8900" />
              </div>
              {!editId && (
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@business.com" />
                </div>
              )}
              <div className="form-group">
                <label>{editId ? 'New Password (leave blank to keep)' : 'Password'}</label>
                <input className="form-control" type="password" required={!editId} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
              </div>
              <div className="form-group">
                <label>Location <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                <select className="form-control" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}>
                  <option value="">— No specific location —</option>
                  {locations.map(l => (
                    <option key={l._id} value={l._id}>{l.name}{l.city ? ` · ${l.city}` : ''}</option>
                  ))}
                </select>
                {locations.length === 0 && (
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>No locations yet — add them in the Locations tab.</p>
                )}
              </div>
            </div>
            <button className="btn btn-primary" type="submit">{editId ? 'Save Changes' : 'Add Staff Member'}</button>
          </form>
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted" style={{ padding: 40 }}>No staff added yet</td></tr>
              )}
              {list.map(ft => (
                <tr key={ft._id}>
                  <td><strong>{ft.name}</strong>{ft.phone && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ft.phone}</div>}</td>
                  <td>{ft.email}</td>
                  <td>
                    {ft.location
                      ? <span className="badge badge-info">📍 {ft.location.name}{ft.location.city ? `, ${ft.location.city}` : ''}</span>
                      : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>All locations</span>
                    }
                  </td>
                  <td>
                    <span className={`badge ${ft.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {ft.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => handleEdit(ft)}>Edit</button>
                    <button className="btn btn-sm" style={{ background: ft.isActive ? '#fef9c3' : '#dcfce7', color: ft.isActive ? '#92400e' : '#15803d' }} onClick={() => handleToggleActive(ft)}>
                      {ft.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ft._id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
